import { FaRegUser } from "react-icons/fa6";
import { MdInfoOutline } from "react-icons/md";
import colors from "tailwindcss/colors";
import {
  AssessmentQuestionType,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyReportRowFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";

import StickyColumnShadow from "@apps/org-settings/components/competencies/sticky-column-shadow";
import { assessmentQuestionIcon } from "@apps/programs/components/compliance-program-form/assessment-group-sidebar/assessment-group-sidebar-question";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import { resultColWidth, stickyColWidth } from "./engagement-survey-report";

export const npsColors: string[] = [
  colors.red[600],
  colors.red[400],
  colors.amber[400],
  colors.emerald[500],
  colors.emerald[700],
];
export const rangeColors: string[] = [
  colors.purple[200],
  colors.purple[300],
  colors.purple[400],
  colors.purple[500],
  colors.purple[700],
];

const getNpsColorValue = (npsScore: number) => {
  if (npsScore <= 0) {
    return npsColors[0];
  }
  if (npsScore <= 30) {
    return npsColors[1];
  }
  if (npsScore <= 50) {
    return npsColors[2];
  }
  if (npsScore <= 70) {
    return npsColors[3];
  }
  return npsColors[4];
};
const getRangeColorValue = (normalizedValue: number) => {
  if (normalizedValue <= 20) {
    return rangeColors[0];
  }
  if (normalizedValue <= 40) {
    return rangeColors[1];
  }
  if (normalizedValue <= 60) {
    return rangeColors[2];
  }
  if (normalizedValue <= 80) {
    return rangeColors[3];
  }
  return rangeColors[4];
};
const isHighContrastBackground = (backgroundColor: string) =>
  [npsColors[2], rangeColors[0], rangeColors[1], "#ffffff"].includes(
    backgroundColor
  );

export enum EngagementSurveyReportTableType {
  NPS,
  RANGE,
}

const EngagementSurveyReportTable = ({
  responders,
  questionRows,
  type,
}: {
  responders: EngagementSurveyReportResponderFragment[];
  questionRows: EngagementSurveyReportRowFragment[];
  type: EngagementSurveyReportTableType;
}) => {
  const label = useLabel();

  return (
    <div className="relative overflow-x-auto border rounded-md w-fit max-w-full">
      <table className="text-sm table-fixed">
        <thead>
          <tr>
            <th
              className={classNames(
                stickyColWidth,
                "p-4 text-left border-b sticky left-0 bg-gray-50 z-10 align-top"
              )}
            >
              <div className="font-medium text-sm">Question</div>
              <StickyColumnShadow />
            </th>
            {responders.map((responder, columnIndex) => {
              const title =
                responder.rollupType ===
                EngagementSurveyRollupType.Organization ? (
                  label("organization", { capitalize: true })
                ) : responder.rollupType === EngagementSurveyRollupType.Team ? (
                  responder.team?.title
                ) : responder.rollupType ===
                  EngagementSurveyRollupType.CareerTrack ? (
                  responder.careerTrack?.title
                ) : responder.rollupType ===
                  EngagementSurveyRollupType.IsManager ? (
                  `${responder.isManager ? "Managers" : "ICs"}`
                ) : (
                  <div className="flex items-center gap-2">
                    <Avatar size="5" user={responder.manager} />{" "}
                    {responder.manager?.name}
                  </div>
                );
              return (
                <th
                  key={`${responder.rollupType}-${responder.team?.id}-${responder.manager?.id}-${responder.careerTrack?.id}-${responder.isManager}`}
                  className={classNames(
                    resultColWidth,
                    columnIndex < responders.length - 1 && "border-r",
                    "p-4 border-b font-medium text-sm align-top bg-gray-50"
                  )}
                >
                  <div className="flex flex-col items-center gap-1">
                    {title}
                    <div className="text-xs flex items-center gap-1">
                      <FaRegUser className="h-3 w-3" />
                      <div>{responder.responderCount}</div>
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {questionRows.map((question, rowIndex) => {
            const QuestionIcon =
              question.question?.__typename === "RangeAssessmentQuestionNode"
                ? assessmentQuestionIcon[AssessmentQuestionType.Range]
                : assessmentQuestionIcon[AssessmentQuestionType.Nps];
            return (
              <tr key={question.question.id}>
                <td
                  className={classNames(
                    stickyColWidth,
                    rowIndex < questionRows.length - 1 && "border-b",
                    "p-4 sticky left-0 z-10 text-sm bg-white"
                  )}
                >
                  <div className="flex flex-col gap-1">
                    <div>{question.title || "Unnamed Section"}</div>
                    {question.question && (
                      <div className="text-xs text-gray-400 flex items-center gap-2">
                        <QuestionIcon className="h-4 w-4" />
                        {question.question?.__typename ===
                        "RangeAssessmentQuestionNode"
                          ? `Range (${question.question?.startValue}-${question.question?.endValue})`
                          : "eNPS"}
                      </div>
                    )}
                  </div>
                  <StickyColumnShadow />
                </td>

                {responders.map((responder, columnIndex) => {
                  const rollup = question.rollups.edges
                    .map((edge) => assertNonNull(edge?.node))
                    .find(
                      (rollup) =>
                        rollup.rollupType === responder.rollupType &&
                        rollup.manager?.id === responder.manager?.id &&
                        rollup.team?.id === responder.team?.id &&
                        rollup.careerTrack?.id === responder.careerTrack?.id &&
                        rollup.isManager === responder.isManager
                    );

                  const value = rollup?.value ? rollup.value : 0;
                  const backgroundColor =
                    responder.responderCount === 0 || responder.isHidden
                      ? "#ffffff"
                      : question.question?.__typename ===
                        "RangeAssessmentQuestionNode"
                      ? getRangeColorValue(
                          (value / question.question?.endValue) * 100
                        )
                      : getNpsColorValue(value);
                  return (
                    <td
                      key={`${responder.rollupType}-${responder.team?.id}-${responder.manager?.id}-${responder.careerTrack?.id}-${responder.isManager}`}
                      className={classNames(
                        resultColWidth,
                        rowIndex < questionRows.length - 1 && "border-b",
                        columnIndex < responders.length - 1 && "border-r",
                        !isHighContrastBackground(backgroundColor) &&
                          "text-white",
                        "p-4 text-center align-middle"
                      )}
                      style={{ backgroundColor }}
                    >
                      {responder.responderCount === 0 ? (
                        <div className="text-sm">N/A</div>
                      ) : responder.isHidden ? (
                        <div className="flex flex-col items-center gap-2.5 text-gray-400">
                          <Tooltip text="To keep identities anonymous, we cannot display data when there is less than 3 people within a group.">
                            <span>
                              <MdInfoOutline className="h-4 w-4" />
                            </span>
                          </Tooltip>
                          <div className="text-sm">
                            Not enough responses to show results
                          </div>
                        </div>
                      ) : question.question?.__typename ===
                        "RangeAssessmentQuestionNode" ? (
                        <div className="text-sm font-semibold flex items-center justify-center gap-1">
                          {value}
                          <div className="opacity-50">
                            / {question.question?.endValue}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm font-semibold">{value}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EngagementSurveyReportTable;
